import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, Video, ShieldCheck } from "lucide-react";

/**
 * Donation thank-you page. Handles BOTH providers:
 *  - Stripe:  ?session_id=cs_...
 *  - PayPal:  ?provider=paypal&token=<orderId>  (PayPal appends ?token=)
 * Polls the matching status endpoint until the payment is confirmed, then shows
 * whether an AI video is generating and reminds the donor about host moderation.
 */
export default function DonationThanks() {
  const [params] = useSearchParams();
  const provider = params.get("provider") === "paypal" ? "paypal" : "stripe";
  const sessionId = params.get("session_id");
  const orderId = params.get("token") || params.get("order_id");
  const key = provider === "paypal" ? orderId : sessionId;

  const [status, setStatus] = useState({ payment_status: "pending" });
  const [ticks, setTicks] = useState(0);

  useEffect(() => {
    if (!key || ticks > 40) return;
    const t = setTimeout(async () => {
      try {
        const url = provider === "paypal"
          ? `/donation/paypal/status/${orderId}`
          : `/donation/status/${sessionId}`;
        const { data } = await api.get(url);
        setStatus(data);
        if (data.payment_status === "paid" && (data.video_status === "ready" || data.video_status === "failed" || data.video_status === "none")) return;
      } catch { /* retry */ }
      setTicks((n) => n + 1);
    }, 2000);
    return () => clearTimeout(t);
  }, [key, ticks, provider, orderId, sessionId]);

  const paid = status.payment_status === "paid";
  const videoPending = paid && status.video_status === "pending";

  return (
    <div className="max-w-[720px] mx-auto px-6 lg:px-12 py-24" data-testid="donation-thanks-page">
      <div className="surface-card p-10 text-center">
        {!paid ? (
          <>
            <Loader2 size={64} className="mx-auto text-[var(--ff-red)] mb-4 animate-spin" />
            <h1 className="font-display text-3xl mb-3">CONFIRMING PAYMENT…</h1>
            <p className="text-[var(--ff-text-dim)] font-mono-ui text-sm">
              Waiting for {provider === "paypal" ? "PayPal" : "Stripe"} to confirm. This usually takes just a moment.
            </p>
          </>
        ) : (
          <>
            <CheckCircle2 size={72} className="mx-auto text-green-400 mb-4" data-testid="donation-thanks-paid" />
            <h1 className="font-display text-4xl mb-3">THANK YOU!</h1>
            <p className="text-[var(--ff-text-dim)] font-mono-ui text-sm mb-6">
              Your donation went through. 🎉
            </p>

            {videoPending && (
              <div className="surface-card p-4 mb-4 flex items-center gap-3 text-left" data-testid="donation-thanks-video-pending">
                <Video size={22} className="text-[var(--ff-red)] shrink-0 animate-pulse" />
                <div className="text-sm">
                  <div className="font-display text-lg">Your AI video is generating…</div>
                  <div className="text-[var(--ff-text-dim)] font-mono-ui text-xs">This takes ~1–3 minutes. You can safely close this page.</div>
                </div>
              </div>
            )}
            {status.video_status === "failed" && (
              <div className="surface-card p-4 mb-4 border-yellow-500/40 text-yellow-300 text-sm font-mono-ui" data-testid="donation-thanks-video-failed">
                We couldn&apos;t generate the video this time, but your text alert will still reach the host.
              </div>
            )}

            <div className="flex items-start gap-3 text-left bg-[var(--ff-bg)] border border-[var(--ff-border)] p-4 rounded mb-6">
              <ShieldCheck size={20} className="text-[var(--ff-text-dim)] shrink-0 mt-0.5" />
              <p className="text-[var(--ff-text-dim)] font-mono-ui text-xs leading-relaxed">
                The host reviews every alert before it airs on stream. If they approve it, it&apos;ll pop up live for everyone to see.
              </p>
            </div>

            <div className="flex justify-center gap-3">
              <Link to="/shows" className="btn-primary" data-testid="donation-thanks-shows">Browse Shows</Link>
              <Link to="/" className="btn-ghost" data-testid="donation-thanks-home">Back Home</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
